// ────────────────────────────────────────────────────────────────────────────
//  Recruitment.tsx — Stellen-Links + Bewerbungen
//
//  Tabs:
//   1. Stellen — Liste + neue erstellen + kopieren/deaktivieren/löschen
//   2. Bewerbungen — Filter nach Status, Detail-View mit Notizen + Status-Update
//
//  Gating:
//   - Org nötig (sonst Lock-Screen)
//   - Recruitment-Extension (oder Business-Tier) nötig (sonst Shop-CTA)
// ────────────────────────────────────────────────────────────────────────────

import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useT, useLangCode } from '../i18n'
import { useExtensions } from '../lib/useExtensions'
import { useOrg } from '../lib/useOrg'
import {
  useJobLinks, useApplications,
  createJobLink, updateJobLink, deleteJobLink,
  updateApplication, getApplication, getPublicJob,
  type JobLink, type JobApplication,
} from '../lib/useRecruitment'

export function Recruitment() {
  const { has, loading: extLoading } = useExtensions()
  const { org, loading: orgLoading } = useOrg()

  if (extLoading || orgLoading) return <Spinner />

  // Recruitment ist eine eigene Extension — sie ist NICHT Teil der
  // Business-Pakete. Org ist Pflicht, weil Bewerbungen org-bound sind.
  // Wer keinen Zugriff hat, sieht den Bewerber-Einstieg (Code-Eingabe)
  // statt einer Lock-Hürde.
  if (!org) return <ApplicantEntry reason="no-org" />
  if (!has('Recruitment')) return <ApplicantEntry reason="no-extension" />

  return <Panel />
}

// ── Bewerber-Einstieg (statt Lock-Screen) ──────────────────────────────────
function ApplicantEntry({ reason }: { reason: 'no-org' | 'no-extension' }) {
  const t = useT()
  const nav = useNavigate()
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    const c = code.trim().toUpperCase()
    if (!c) return
    setBusy(true); setErr(null)
    try {
      // Code prüfen — bei Erfolg navigieren wir zur öffentlichen Apply-Page.
      // Schlägt fehl → wir zeigen die Fehlermeldung inline statt zu navigieren.
      await getPublicJob(c)
      nav(`/apply/${c}`)
    } catch {
      setErr(t('recruit.applyCodeInputErr'))
    } finally { setBusy(false) }
  }

  return (
    <div className="max-w-2xl mx-auto px-4 md:px-6 py-12 md:py-20 space-y-5">
      {/* Hauptkarte: Bewerbungs-Code-Eingabe */}
      <section className="glass-strong p-6 md:p-10 text-center">
        <div className="text-6xl mb-4">📋</div>
        <h1 className="text-2xl md:text-3xl font-bold">{t('recruit.haveCodeTitle')}</h1>
        <p className="text-app-text-dim mt-3 max-w-md mx-auto leading-relaxed">{t('recruit.haveCodeSub')}</p>

        <form onSubmit={submit} className="mt-6 space-y-3 max-w-sm mx-auto text-left">
          <input
            type="text"
            value={code}
            onChange={e => setCode(e.target.value.toUpperCase())}
            placeholder={t('recruit.applyCodeInputPh')}
            maxLength={20}
            autoComplete="off"
            className="input-base text-center text-xl font-mono tracking-widest uppercase"
          />
          {err && <div className="text-sm text-coral bg-coral/10 border border-coral/30 rounded-lg p-2.5">{err}</div>}
          <button type="submit" disabled={busy || code.trim().length < 4} className="btn-primary w-full disabled:opacity-50">
            {busy ? '…' : t('recruit.haveCodeBtn')}
          </button>
        </form>

        <div className="mt-5 text-xs text-app-text-mute">
          <Link to="/apply" className="hover:text-app-text underline decoration-app-border-2 hover:decoration-accent transition">
            {t('recruit.haveCodeOpen')}
          </Link>
        </div>
      </section>

      {/* Sekundär: Recruiter-Hint (klein, dezent) */}
      <section
        className="rounded-3xl p-5 text-center"
        style={{ background: 'var(--bg-elev)', border: '1px solid var(--border)' }}
      >
        <p className="text-sm text-app-text-dim">
          {t('recruit.recruiterHint')}{' '}
          {reason === 'no-extension' ? (
            <Link to="/app/shop" className="text-accent hover:underline font-medium">
              {t('recruit.recruiterShop')}
            </Link>
          ) : (
            <Link to="/app/org" className="text-accent hover:underline font-medium">
              {t('recruit.recruiterOrg')}
            </Link>
          )}
        </p>
      </section>
    </div>
  )
}

function Panel() {
  const t = useT()
  const [tab, setTab] = useState<'links' | 'apps'>('links')
  return (
    <div className="max-w-5xl mx-auto px-3 sm:px-4 md:px-6 py-5 sm:py-7 md:py-10 space-y-4 sm:space-y-5">
      <header>
        <h1 className="text-3xl md:text-4xl font-bold">{t('recruit.title')}</h1>
        <p className="text-sm text-app-text-dim mt-1.5">{t('recruit.sub')}</p>
      </header>

      <nav>
        <div className="inline-flex gap-1 p-1 rounded-xl"
             style={{ background: 'var(--bg-input)', border: '1px solid var(--border)' }}>
          {([
            { id: 'links' as const, l: t('recruit.tabLinks') },
            { id: 'apps' as const,  l: t('recruit.tabApplications') },
          ]).map(o => (
            <button key={o.id} onClick={() => setTab(o.id)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                tab === o.id ? 'bg-accent text-white shadow-md shadow-accent/30' : 'text-app-text-dim hover:text-app-text'
              }`}>
              {o.l}
            </button>
          ))}
        </div>
      </nav>

      {tab === 'links' ? <LinksTab /> : <ApplicationsTab />}
    </div>
  )
}

// ── Tab: Stellen-Links ─────────────────────────────────────────────────────
function LinksTab() {
  const t = useT()
  const { items, loading, refresh } = useJobLinks()
  const [showCreate, setShowCreate] = useState(false)

  return (
    <section className="glass p-5 md:p-7">
      <header className="mb-4 flex items-center justify-between">
        <h3 className="font-semibold flex items-center gap-2"><span className="text-lg">📋</span>{t('recruit.tabLinks')}</h3>
        <button onClick={() => setShowCreate(true)} className="btn-primary text-sm py-2 px-4">{t('recruit.newLink')}</button>
      </header>
      {loading ? <Spinner small /> : items.length === 0 ? (
        <div className="text-sm text-app-text-mute text-center py-8">{t('recruit.linksEmpty')}</div>
      ) : (
        <ul className="space-y-3">
          {items.map(l => <LinkRow key={l.id} l={l} t={t} refresh={refresh} />)}
        </ul>
      )}
      {showCreate && <CreateLinkModal onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); refresh() }} />}
    </section>
  )
}

function LinkRow({ l, t, refresh }: { l: JobLink; t: any; refresh: () => void }) {
  const [copied, setCopied] = useState(false)
  const copy = async () => {
    try { await navigator.clipboard.writeText(l.publicUrl); setCopied(true); setTimeout(() => setCopied(false), 1500) } catch {}
  }
  const toggleActive = async () => {
    try { await updateJobLink(l.id, { isActive: !l.isActive }); refresh() } catch (e: any) { alert(e?.message) }
  }
  const handleDelete = async () => {
    if (!confirm(t('recruit.linkDeleteConfirm', { title: l.title }))) return
    try { await deleteJobLink(l.id); refresh() } catch (e: any) { alert(e?.message) }
  }
  return (
    <li className="rounded-xl glass-flat p-4">
      <div className="flex flex-wrap items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className="font-semibold">{l.title}</h4>
            <span className={`text-[10px] font-bold tracking-wider uppercase px-2 py-0.5 rounded-full border ${
              l.isActive ? 'border-teal/30 bg-teal/15 text-teal' : 'border-app-border text-app-text-mute'
            }`}>
              {l.isActive ? t('recruit.linkActive') : t('recruit.linkInactive')}
            </span>
            <span className="text-xs text-app-text-mute">{t('recruit.linkUsedCount', { n: l.usedCount })}</span>
          </div>
          {l.description && <p className="text-sm text-app-text-dim mt-1.5 leading-relaxed">{l.description}</p>}
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            <code className="text-xs font-mono px-2.5 py-1.5 rounded-lg bg-accent/10 border border-accent/25 text-accent break-all">
              {l.publicUrl}
            </code>
            <button onClick={copy} className="text-xs px-2.5 py-1.5 rounded-lg glass-flat hover:bg-app-elev-2 transition">
              {copied ? t('recruit.linkCopied') : t('recruit.linkCopy')}
            </button>
          </div>
        </div>
      </div>
      <div className="mt-3 flex gap-2 flex-wrap text-xs">
        <button onClick={toggleActive} className="px-3 py-1.5 rounded-lg glass-flat hover:bg-app-elev-2 transition">
          {l.isActive ? t('recruit.linkDeactivate') : t('recruit.linkActivate')}
        </button>
        <button onClick={handleDelete} className="px-3 py-1.5 rounded-lg text-coral hover:bg-coral/10 transition">
          🗑 {t('recruit.linkDelete')}
        </button>
      </div>
    </li>
  )
}

function CreateLinkModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const t = useT()
  const [title, setTitle] = useState('')
  const [desc, setDesc] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setBusy(true); setErr(null)
    try {
      await createJobLink({ title: title.trim(), description: desc.trim() || undefined })
      onCreated()
    } catch (e: any) { setErr(e?.message ?? 'failed'); setBusy(false) }
  }

  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4 backdrop-blur-2xl"
         style={{ background: 'rgba(0,0,0,0.72)' }} onClick={onClose}>
      <div className="w-full max-w-lg glass-strong p-6" onClick={e => e.stopPropagation()}>
        <h3 className="font-bold text-lg mb-4">{t('recruit.newLink')}</h3>
        <form onSubmit={submit} className="space-y-3">
          <label className="block">
            <span className="text-xs font-bold uppercase tracking-wider text-app-text-dim mb-1.5 block">{t('recruit.linkTitleField')}</span>
            <input type="text" required value={title} onChange={e => setTitle(e.target.value)} placeholder={t('recruit.linkTitlePh')} className="input-base" />
          </label>
          <label className="block">
            <span className="text-xs font-bold uppercase tracking-wider text-app-text-dim mb-1.5 block">{t('recruit.linkDescField')}</span>
            <textarea value={desc} onChange={e => setDesc(e.target.value)} placeholder={t('recruit.linkDescPh')} rows={4} className="input-base" />
          </label>
          {err && <div className="text-sm text-coral bg-coral/10 border border-coral/30 rounded-lg p-2.5">{err}</div>}
          <div className="flex gap-2 justify-end">
            <button type="button" onClick={onClose} className="btn-ghost text-sm py-2 px-4">{t('common.cancel')}</button>
            <button type="submit" disabled={busy || !title.trim()} className="btn-primary text-sm py-2 px-4 disabled:opacity-50">
              {busy ? '…' : t('recruit.linkCreate')}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ── Tab: Bewerbungen ───────────────────────────────────────────────────────
function ApplicationsTab() {
  const t = useT()
  const [filter, setFilter] = useState<'all' | 'New' | 'Viewed' | 'Contacted' | 'Accepted' | 'Rejected'>('all')
  const { items, loading, refresh } = useApplications(filter)
  const [detailId, setDetailId] = useState<number | null>(null)

  return (
    <section className="glass p-5 md:p-7">
      <header className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h3 className="font-semibold flex items-center gap-2"><span className="text-lg">📥</span>{t('recruit.tabApplications')}</h3>
        <div className="overflow-x-auto -mx-2 px-2">
          <div className="inline-flex gap-1 p-1 rounded-xl"
               style={{ background: 'var(--bg-input)', border: '1px solid var(--border)' }}>
            {([
              { v: 'all' as const,        l: t('recruit.appsAll') },
              { v: 'New' as const,        l: t('recruit.appsNew') },
              { v: 'Viewed' as const,     l: t('recruit.appsViewed') },
              { v: 'Contacted' as const,  l: t('recruit.appsContacted') },
              { v: 'Accepted' as const,   l: t('recruit.appsAccepted') },
              { v: 'Rejected' as const,   l: t('recruit.appsRejected') },
            ]).map(o => (
              <button key={o.v} onClick={() => setFilter(o.v)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition whitespace-nowrap ${
                  filter === o.v ? 'bg-accent text-white' : 'text-app-text-dim hover:text-app-text'
                }`}>
                {o.l}
              </button>
            ))}
          </div>
        </div>
      </header>
      {loading ? <Spinner small /> : items.length === 0 ? (
        <div className="text-sm text-app-text-mute text-center py-8">{t('recruit.appsEmpty')}</div>
      ) : (
        <ul className="space-y-2">
          {items.map(a => <AppRow key={a.id} a={a} t={t} onClick={() => setDetailId(a.id)} />)}
        </ul>
      )}
      {detailId && <AppDetailModal id={detailId} onClose={() => setDetailId(null)} onChanged={refresh} />}
    </section>
  )
}

function AppRow({ a, t, onClick }: { a: JobApplication; t: any; onClick: () => void }) {
  const lang = useLangCode()
  const initials = `${a.firstName[0] ?? ''}${a.lastName[0] ?? ''}`.toUpperCase()
  return (
    <li>
      <button onClick={onClick} className="w-full text-left rounded-xl glass-flat hover:bg-app-elev-2 transition p-3 flex items-center gap-3">
        <div className="w-10 h-10 rounded-full grid place-items-center text-xs font-bold text-white bg-gradient-to-br from-coral to-accent2">
          {initials || '?'}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold truncate">{a.firstName} {a.lastName}</div>
          <div className="text-xs text-app-text-mute truncate">{a.email}{a.linkTitle && ` · ${a.linkTitle}`}</div>
        </div>
        <StatusPill status={a.status} t={t} />
        <div className="text-[10px] text-app-text-mute hidden md:block shrink-0">
          {new Date(a.submittedAt).toLocaleDateString(lang, { day: '2-digit', month: '2-digit' })}
        </div>
      </button>
    </li>
  )
}

function StatusPill({ status, t }: { status: string; t: any }) {
  const map: any = {
    New:       { color: '#33d670', bg: 'rgba(51,214,112,0.15)',  border: 'rgba(51,214,112,0.30)',  text: t('recruit.appsNew') },
    Viewed:    { color: '#8a44ee', bg: 'rgba(138,68,238,0.15)', border: 'rgba(138,68,238,0.30)', text: t('recruit.appsViewed') },
    Contacted: { color: '#3a62ff', bg: 'rgba(58,98,255,0.15)',   border: 'rgba(58,98,255,0.30)',   text: t('recruit.appsContacted') },
    Accepted:  { color: '#33d670', bg: 'rgba(51,214,112,0.20)',  border: 'rgba(51,214,112,0.40)',  text: t('recruit.appsAccepted') },
    Rejected:  { color: '#ff5b5c', bg: 'rgba(255,91,92,0.15)',   border: 'rgba(255,91,92,0.30)',   text: t('recruit.appsRejected') },
  }
  const m = map[status] ?? map.New
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border shrink-0"
          style={{ background: m.bg, color: m.color, borderColor: m.border }}>
      {m.text}
    </span>
  )
}

// ── App-Detail-Modal ───────────────────────────────────────────────────────
function AppDetailModal({ id, onClose, onChanged }: { id: number; onClose: () => void; onChanged: () => void }) {
  const t = useT()
  const lang = useLangCode()
  const [app, setApp] = useState<JobApplication | null>(null)
  const [loading, setLoading] = useState(true)
  const [notes, setNotes] = useState('')
  const [status, setStatus] = useState<string>('')
  const [busy, setBusy] = useState(false)
  const [ok, setOk] = useState(false)

  useEffect(() => {
    getApplication(id)
      .then(a => { setApp(a); setNotes(a.internalNotes ?? ''); setStatus(a.status); setLoading(false) })
      .catch(() => setLoading(false))
  }, [id])

  const save = async () => {
    setBusy(true); setOk(false)
    try {
      await updateApplication(id, { status, internalNotes: notes })
      setOk(true); onChanged()
      setTimeout(() => setOk(false), 1500)
    } catch (e: any) { alert(e?.message) }
    finally { setBusy(false) }
  }

  return (
    <div className="fixed inset-0 z-50 grid place-items-start sm:place-items-center p-0 sm:p-4 backdrop-blur-2xl"
         style={{ background: 'rgba(0,0,0,0.72)' }} onClick={onClose}>
      <div className="w-full max-w-2xl glass-strong p-5 md:p-7 sm:rounded-3xl rounded-none max-h-screen overflow-y-auto"
           onClick={e => e.stopPropagation()}>
        <header className="mb-4 flex items-start justify-between gap-3">
          <button onClick={onClose} className="text-sm text-app-text-mute hover:text-app-text">{t('recruit.appBack')}</button>
          <div className="flex-1" />
          {app && <StatusPill status={app.status} t={t} />}
        </header>

        {loading || !app ? <Spinner small /> : (
          <>
            <h2 className="text-2xl font-bold">{app.firstName} {app.lastName}</h2>
            <div className="text-sm text-app-text-dim">{app.linkTitle}</div>

            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-5">
              <Field label={t('recruit.appEmail')} value={<a href={`mailto:${app.email}`} className="text-accent hover:underline">{app.email}</a>} />
              {app.phone && <Field label={t('recruit.appPhone')} value={<a href={`tel:${app.phone}`} className="text-accent hover:underline">{app.phone}</a>} />}
              <Field label={t('recruit.appAppliedAt')} value={new Date(app.submittedAt).toLocaleDateString(lang, { day: '2-digit', month: 'long', year: 'numeric' })} />
            </dl>

            {app.coverLetter && (
              <section className="mt-5">
                <div className="text-xs font-bold uppercase tracking-wider text-app-text-dim mb-1.5">{t('recruit.appCoverLetter')}</div>
                <div className="rounded-xl glass-flat p-3.5 text-sm whitespace-pre-wrap leading-relaxed">{app.coverLetter}</div>
              </section>
            )}

            <section className="mt-5">
              <div className="text-xs font-bold uppercase tracking-wider text-app-text-dim mb-1.5">{t('recruit.appAttachments')}</div>
              {app.attachments.length === 0 ? (
                <div className="text-sm text-app-text-mute">{t('recruit.appNoAttach')}</div>
              ) : (
                <ul className="space-y-1.5">
                  {app.attachments.map((a, i) => (
                    <li key={i}>
                      <a href={a.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-sm text-accent hover:underline">
                        📎 {a.name} <span className="text-app-text-mute">({Math.round(a.bytes / 1024)} KB)</span>
                      </a>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section className="mt-5">
              <label className="block">
                <span className="text-xs font-bold uppercase tracking-wider text-app-text-dim mb-1.5 block">Status</span>
                <select value={status} onChange={e => setStatus(e.target.value)} className="input-base">
                  <option value="New">{t('recruit.appsNew')}</option>
                  <option value="Viewed">{t('recruit.appsViewed')}</option>
                  <option value="Contacted">{t('recruit.appsContacted')}</option>
                  <option value="Accepted">{t('recruit.appsAccepted')}</option>
                  <option value="Rejected">{t('recruit.appsRejected')}</option>
                </select>
              </label>
            </section>

            <section className="mt-3">
              <label className="block">
                <span className="text-xs font-bold uppercase tracking-wider text-app-text-dim mb-1.5 block">{t('recruit.appNotes')}</span>
                <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder={t('recruit.appNotesPh')} rows={3} className="input-base" />
              </label>
            </section>

            <div className="mt-5 flex gap-2 justify-end items-center">
              {ok && <span className="text-sm text-teal">{t('recruit.appUpdated')}</span>}
              <button onClick={onClose} className="btn-ghost text-sm py-2 px-4">{t('common.close')}</button>
              <button onClick={save} disabled={busy} className="btn-primary text-sm py-2 px-4 disabled:opacity-50">
                {busy ? '…' : t('recruit.appUpdate')}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <div className="text-[11px] font-bold uppercase tracking-wider text-app-text-mute">{label}</div>
      <div className="text-sm mt-0.5">{value}</div>
    </div>
  )
}

function Spinner({ small }: { small?: boolean }) {
  const sz = small ? 'w-6 h-6 border-2' : 'w-10 h-10 border-4'
  return (
    <div className="flex items-center justify-center py-12">
      <div className={`${sz} rounded-full border-app-border border-t-accent animate-spin`} />
    </div>
  )
}
